import { SCREEN_PATHS } from "../constants";
import { NavigateFunction, NavigateOptions } from "react-router";
import { DataDef, DataSources } from "../apis/data";
import { AtticusMySQL } from "../apis/AtticusMySQL";

export type FlowNext = { outcome: string; screen: string };
export type SectionPossible = { answered: string; value: string };

export type FlowScreen = {
  name: string;
  section: string | SectionPossible[];
  pathSuffix: string;
  screenComponent: JSX.Element;
  next: FlowNext[] | null;
  data?: DataDef[];
  first?: boolean;
  completedAll?: boolean;
};

export class AtticusScreenFlow {
  constructor(
    public flowScreens: FlowScreen[],
    public flowPath: string,
    public sections: string[],
    public screens?: string[],
    public disabled?: boolean
  ) {}

  getCurrentFlowScreen(screenName?: string): FlowScreen | undefined {
    const currentScreenFind = this.flowScreens.filter(
      (screen: FlowScreen) => screen.pathSuffix === screenName
    );
    return currentScreenFind?.length ? currentScreenFind[0] : undefined;
  }

  getFlowFirstScreen(): string {
    const firstScreen = this.flowScreens.filter(
      (screen: FlowScreen) => screen.first
    );
    if (firstScreen?.length) {
      return `${SCREEN_PATHS.flow}/${this.flowPath}/${firstScreen[0].pathSuffix}`;
    } else {
      return SCREEN_PATHS.home;
    }
  }

  getNextScreen(currentScreen: FlowScreen, outcome: string): string {
    const nextScreenName = currentScreen.next?.filter(
      (screen: FlowNext) => screen.outcome === outcome
    )[0].screen;
    const nextScreen = this.flowScreens.filter(
      (screen: FlowScreen) => screen.name === nextScreenName
    )[0];
    return nextScreen
      ? `${SCREEN_PATHS.flow}/${this.flowPath}/${nextScreen.pathSuffix}`
      : SCREEN_PATHS.not_found;
  }

  navigateToNext(
    currentScreen: FlowScreen,
    outcome: string,
    navigate: NavigateFunction,
    options?: NavigateOptions
  ): void {
    const nextPath = this.getNextScreen(currentScreen, outcome);
    navigate(nextPath, options);
  }

  getSection(currentScreen?: FlowScreen): string | undefined {
    if (currentScreen === undefined) return undefined;
    if (typeof currentScreen.section === "string") {
      return currentScreen.section;
    } else {
      // TODO: need to determine this based on data available
      return currentScreen.section[0].value;
    }
  }

  getSectionIndex(currentScreen?: FlowScreen): number | undefined {
    if (currentScreen === undefined) return undefined;
    const currentSectionTitle = this.getSection(currentScreen);
    let sectionIndex;
    this.sections.forEach((section: string, index: number) => {
      if (currentSectionTitle === section) sectionIndex = index;
    });
    return sectionIndex;
  }

  getTotalSteps(): number | undefined {
    return this.sections?.length;
  }

  getCurrentStep(currentScreen?: FlowScreen): number | undefined {
    if (currentScreen === undefined) return undefined;
    let step;
    this.sections?.forEach((section: string, index: number) => {
      if (section === currentScreen.section) {
        step = index + 1;
      }
    });
    return step;
  }

  /**
   * useful for auto-saving simple data fields
   *  alternatively a screen component can manually fetch it's own data
   * @param currentScreen
   * @param id
   * @param data
   */
  // TODO: set a data type
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  async saveData(
    currentScreen?: FlowScreen,
    id?: number | string,
    data?: Record<string, string | number | boolean>
  ): Promise<boolean[]> {
    const results = [];
    try {
      if (!currentScreen) throw new Error("currentScreen undefined");
      if (currentScreen.data) {
        // loop over data sources
        for (const screenDataDef of currentScreen.data) {
          let result: boolean;
          switch (screenDataDef.source) {
            case DataSources.cognito: {
              result = data ? await this.saveCognitoData(data) : false;
              break;
            }
            case DataSources.mysql: {
              result = await AtticusMySQL.saveScreenData(
                screenDataDef,
                id,
                data
              );
              break;
            }
            // case DataSources.users: {
            //   result = data ? await this.saveUserData(data) : false;
            //   break;
            // }
            default:
              result = false;
          }
          results.push(result);
        }
      }
    } catch (error) {
      console.error(error);
      throw error;
    }
    return Promise.resolve(results);
  }

  // should be moved in an API
  async saveCognitoData(
    data: Record<string, string | number | boolean>
  ): Promise<boolean> {
    // TODO: save these fields to aws:cognito user pool
    console.log(`saveCognitoData: ${JSON.stringify(data)}`);
    return Promise.resolve(true);
  }

  // should be moved in an API
  async saveEstateData(
    data: Record<string, string | number | boolean>
  ): Promise<boolean> {
    // TODO: save these fields to estate laravel api
    console.log(`saveEstateData: ${JSON.stringify(data)}`);
    return Promise.resolve(true);
  }

  // should be moved in an API
  async saveUserData(
    data: Record<string, string | number | boolean>
  ): Promise<boolean> {
    // TODO: save these fields to estate laravel api
    console.log(`saveUserData: ${JSON.stringify(data)}`);
    return Promise.resolve(true);
  }

}
